import React, { useMemo, Fragment, useState, useEffect } from 'react'
import moment from 'moment'
import Highcharts from 'highcharts/highstock'
import HighchartsReact from 'highcharts-react-official'
import noDataToDisplay from 'highcharts/modules/no-data-to-display'
import { Grid } from '@material-ui/core'

import { callAuthorizedGraphQLEndpoint } from 'Module/graphQL'
import { ToggleButton } from 'Components/Form'

import { getCountryUsers } from './gql'
import { getCategories, getSeries, getChartOptions } from './utils'
import Filters from './Filters'

noDataToDisplay(Highcharts)

const Chart = ({ options, channels, country, classes, ...props }) => {
  const { monthOptions, yearOptions } = getChartOptions()
  const currentMonth = moment().format('MM')
  const [chart, setChart] = useState({
    years: ['all'],
    type: 'year',
    series: [],
    months: [currentMonth],
    isLoading: false,
  })
  const [data, setData] = useState({
    recruitmentChannelsLog: [],
    brandAmbassadors: [],
  })

  const { categories, sortedKeyFormat } = useMemo(() => getCategories(chart), [
    chart.years,
    chart.months,
    chart.type,
  ])

  const highchartsReactOptions = useMemo(
    () => ({
      title: { text: '' },
      credits: {
        enabled: false,
      },
      legend: {
        layout: 'vertical',
        align: 'right',
        verticalAlign: 'middle',
      },
      xAxis: {
        categories,
        title: {
          enabled: false,
        },
      },
      yAxis: {
        title: {
          enabled: false,
        },
      },
      noData: {
        position: {
          x: 0,
          y: 0,
          align: 'center',
          verticalAlign: 'middle',
        },
      },
      series: chart.series,
      ...options,
    }),
    [options, chart.series],
  )

  useEffect(() => {
    setChart(chart => ({ ...chart, isLoading: true }))
    const getData = async () => {
      if (data.recruitmentChannelsLog.length === 0) {
        const result = await callAuthorizedGraphQLEndpoint(getCountryUsers, {
          id: country.id,
        })

        if (!result || typeof result.json !== 'function') {
          console.error('Failed to fetch country users data')
          return
        }

        const { data } = await result.json()
        setData({
          recruitmentChannelsLog: data.country.recruitmentChannelsLog,
          brandAmbassadors: data.country.brandAmbassadors.nodes,
        })
      }
    }
    getData()
    setChart(chart => ({ ...chart, isLoading: false }))
  }, [])

  useEffect(() => {
    if (chart.isLoading) return

    const series = getSeries({
      recruitmentChannelsLog: data.recruitmentChannelsLog,
      brandAmbassadors: data.brandAmbassadors,
      chart,
      sortedKeyFormat,
      categories,
    })

    setChart(chart => ({ ...chart, series }))
  }, [data, sortedKeyFormat, categories, chart.isLoading])

  return (
    <Fragment>
      <Filters
        classes={classes}
        chart={chart}
        setChart={setChart}
        monthOptions={monthOptions}
        yearOptions={yearOptions}
        currentMonth={currentMonth}
      />
      <HighchartsReact
        {...props}
        highcharts={Highcharts}
        options={highchartsReactOptions}
      />
      <Grid container justifyContent="center">
        <ToggleButton
          options={[
            { value: 'month', label: 'Month' },
            { value: 'year', label: 'Year' },
          ]}
          value={chart.type}
          onChange={type =>
            setChart(chart => ({ ...chart, type, month: currentMonth }))
          }
        />
      </Grid>
    </Fragment>
  )
}

export default Chart
